// "להפוך עיצוב קיים לבאנר מונפש": מעלים PDF / תמונה / כמה פריימים, בוחרים אתרים וגדלים, והמערכת בונה את כל הגדלים.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { ErrorBox, Field, Loading, useMe } from '../components/ui';
import Icon from '../components/Icon';
import { SitePick, type MediaPick } from './QuickCampaign';

interface FrameItem { id: string; url: string; label: string }
const MOTIONS: [string, string, string][] = [
  ['fade', 'מעבר רך', 'כל פריים נמוג לתוך הבא. הבחירה הבטוחה.'],
  ['zoom', 'התקרבות איטית', 'תנועה עדינה בתוך כל פריים, ומעבר רך בין פריימים.'],
  ['slide', 'החלקה', 'כל פריים נכנס מהצד. אנרגטי יותר.'],
];

export default function ImportDesign() {
  const me = useMe();
  const nav = useNavigate();
  const [clients, setClients] = useState<{ id: string; name: string }[] | null>(null);
  const [clientId, setClientId] = useState('');
  const [newClient, setNewClient] = useState('');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [frames, setFrames] = useState<FrameItem[]>([]);
  const [media, setMedia] = useState<any[] | null>(null);
  const [picks, setPicks] = useState<MediaPick[]>([]);
  const [newSite, setNewSite] = useState('');
  const [motion, setMotion] = useState('fade');
  const [frameMs, setFrameMs] = useState(2400);
  const [cta, setCta] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/clients').then(setClients).catch((e) => setError(e.message));
    api.get('/api/quick/media').then(setMedia).catch(() => setMedia([]));
  }, []);

  const ensureClient = async (): Promise<string | null> => {
    if (clientId) return clientId;
    if (!newClient.trim()) { setError('בחרו לקוח או הקלידו שם ללקוח חדש'); return null; }
    const c = await api.post('/api/clients', { name: newClient.trim() });
    setClientId(c.id); setClients((x) => [...(x ?? []), c]);
    return c.id as string;
  };

  const onFiles = async (files: File[]) => {
    if (!files.length) return;
    setError(null);
    try {
      setBusy('מעלה…');
      const cid = await ensureClient();
      if (!cid) return;
      const list = files;
      const pdf = list.find((f) => /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name));
      if (pdf) {
        setBusy('קורא את עמודי ה-PDF…');
        const fd = new FormData(); fd.append('clientId', cid); fd.append('file', pdf);
        const r = await api.upload('/api/quick/pdf-pages', fd);
        setFrames((x) => [...x, ...r.pages.map((p: any) => ({ id: p.id, url: p.url, label: `עמוד ${p.page}` }))].slice(0, 8));
        if (r.truncated) setError(`ה-PDF כולל ${r.totalPages} עמודים. נלקחו 8 הראשונים.`);
      }
      const imgs = list.filter((f) => f.type.startsWith('image/'));
      if (imgs.length) {
        setBusy('מעלה תמונות…');
        const fd = new FormData(); fd.append('clientId', cid); fd.append('kind', 'image'); fd.append('source', 'import:image');
        imgs.forEach((f) => fd.append('file', f));
        const r = await api.upload('/api/assets/upload', fd);
        setFrames((x) => [...x, ...r.map((a: any, i: number) => ({ id: a.id, url: a.url, label: imgs[i]?.name ?? 'תמונה' }))].slice(0, 8));
      }
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };
  const move = (i: number, d: number) => setFrames((x) => { const y = [...x]; const j = i + d; if (j < 0 || j >= y.length) return y; [y[i], y[j]] = [y[j], y[i]]; return y; });

  const toggleSite = (m: any) => setPicks((p) => p.some((x) => x.siteId === m.id) ? p.filter((x) => x.siteId !== m.id) : [...p, { siteId: m.id, name: m.name, sizes: [...m.sizes], known: m.sizes, zipMaxKb: m.zipMaxKb ?? 150, confirmSpec: me.canApproveSpecs }]);
  const addSite = () => { const n = newSite.trim(); if (!n) return; setPicks((p) => [...p, { newName: n, name: n, sizes: [], known: [], zipMaxKb: 150, confirmSpec: me.canApproveSpecs }]); setNewSite(''); };

  const create = async () => {
    setError(null);
    if (!frames.length) { setError('העלו לפחות קובץ אחד'); return; }
    if (!name.trim()) { setError('תנו שם לקמפיין'); return; }
    try { new URL(url); } catch { setError('כתובת הנחיתה לא תקינה — צריכה להתחיל ב-https://'); return; }
    if (!picks.length || picks.some((p) => !p.sizes.length)) { setError('בחרו אתר וגדלים (לכל אתר לפחות גודל אחד)'); return; }
    try {
      setBusy('מקים קמפיין ומתאים את הגדלים…');
      const cid = await ensureClient();
      if (!cid) return;
      const s = await api.post('/api/quick/setup', {
        clientId: cid, brand: { mode: 'plain' },
        media: picks.map((p) => ({ siteId: p.siteId, newName: p.newName, sizes: p.sizes, zipMaxKb: p.zipMaxKb, confirmSpec: p.confirmSpec })),
        campaign: { name: name.trim(), headline: name.trim(), cta: cta.trim() || 'לפרטים', url: url.trim() }, imageAssetId: null, prompt: '',
      });
      await api.post('/api/quick/import-compose', { setId: s.setId, frames: frames.map((f) => f.id), motion, frameMs, cta: cta.trim() });
      try { sessionStorage.setItem(`q-adapted-${s.setId}`, '1'); } catch { /* לא קריטי */ }
      nav(`/new?set=${s.setId}&master=${s.masterVariantId}&campaign=${s.campaignId}&step=6`);
    } catch (e: any) { setError(e.message); setBusy(null); }
  };

  if (!clients || !media) return <Loading />;
  return (
    <div className="stack" style={{ maxWidth: 900, gap: 20 }}>
      <div className="page-title"><div><h1>להפוך עיצוב קיים לבאנר מונפש</h1><p>מעלים PDF, תמונה או כמה פריימים שיצרתם. המערכת מתאימה אותם לכל הגדלים ומוסיפה אנימציה.</p></div></div>
      {busy && <div className="alert info" role="status"><span className="spinner" /> {busy}</div>}
      <ErrorBox error={error} />

      <section className="card stack">
        <h2>1. לקוח וקמפיין</h2>
        <div className="grid cols-2">
          <Field label="לקוח">
            <select value={clientId} onChange={(e) => { setClientId(e.target.value); setNewClient(''); }}>
              <option value="">— לקוח חדש (הקלידו שם) —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          {!clientId && <Field label="שם לקוח חדש"><input value={newClient} onChange={(e) => setNewClient(e.target.value)} /></Field>}
          <Field label="שם הקמפיין *"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="לדוגמה: מבצע סוכות" /></Field>
          <Field label="כתובת נחיתה *" hint="לשם הלחיצה על הבאנר תוביל"><input className="ltr" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" /></Field>
        </div>
      </section>

      <section className="card stack">
        <h2>2. הקבצים שלכם</h2>
        <Field label="PDF, תמונה או כמה תמונות" hint="אפשר לבחור כמה קבצים יחד. כל עמוד ב-PDF הופך לפריים (עד 8 בסך הכול)."><input type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => { const picked = Array.from(e.target.files ?? []); e.target.value = ''; void onFiles(picked); }} /></Field>
        {frames.length > 0 && (
          <ol className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="סדר הפריימים">
            {frames.map((f, i) => (
              <li key={f.id + i} className="row" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
                <strong style={{ width: 24 }}>{i + 1}</strong>
                <img src={f.url} alt={f.label} className="thumb" style={{ width: 96, height: 64 }} />
                <span className="grow small">{f.label}</span>
                <button className="btn small" onClick={() => move(i, -1)} disabled={i === 0} aria-label="הזזה למעלה">▲</button>
                <button className="btn small" onClick={() => move(i, 1)} disabled={i === frames.length - 1} aria-label="הזזה למטה">▼</button>
                <button className="btn small danger" onClick={() => setFrames((x) => x.filter((_, j) => j !== i))}>הסרה</button>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="card stack">
        <h2>3. אנימציה</h2>
        <div className="grid cols-3" role="radiogroup" aria-label="סוג אנימציה">
          {MOTIONS.map(([k, t, d]) => (
            <label key={k} className={`q-brand ${motion === k ? 'sel' : ''}`} style={{ alignItems: 'flex-start' }}>
              <input type="radio" name="motion" checked={motion === k} onChange={() => setMotion(k)} />
              <span><strong>{t}</strong><span className="small muted">{d}</span></span>
            </label>
          ))}
        </div>
        <div className="grid cols-2">
          <Field label={`משך כל פריים: ${(frameMs / 1000).toFixed(1)} שניות`}><input type="range" min={1000} max={5000} step={200} value={frameMs} onChange={(e) => setFrameMs(Number(e.target.value))} /></Field>
          <Field label="כפתור (לא חובה)" hint="אם ריק, לא יתווסף כפתור והעיצוב נשאר כמו שהוא"><input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="לדוגמה: לפרטים" /></Field>
        </div>
      </section>

      <section className="card stack">
        <h2>4. אתרים וגדלים</h2>
        <div className="q-sites">
          {media.map((m) => (
            <label key={m.id} className={`q-brand ${picks.some((p) => p.siteId === m.id) ? 'sel' : ''}`}>
              <input type="checkbox" checked={picks.some((p) => p.siteId === m.id)} onChange={() => toggleSite(m)} />
              <span><strong>{m.name}</strong><span className="small muted ltr">{m.sizes.map((s: any) => `${s.width}x${s.height}`).join(' · ') || 'אין מידות שמורות'}</span></span>
            </label>
          ))}
        </div>
        <div className="row"><input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="אתר חדש — לדוגמה: כל רגע" aria-label="שם אתר חדש" onKeyDown={(e) => { if (e.key === 'Enter') addSite(); }} /><button className="btn" onClick={addSite}>הוספה</button></div>
        {picks.map((p, i) => <SitePick key={(p.siteId ?? '') + p.name} p={p} canApprove={me.canApproveSpecs} onChange={(x) => setPicks((all) => all.map((y, j) => (j === i ? { ...y, ...x } : y)))} onRemove={() => setPicks((all) => all.filter((_, j) => j !== i))} />)}
      </section>

      <div><button className="btn primary big" onClick={create} disabled={!!busy}><Icon name="wand" size={22} />צור באנרים מונפשים</button></div>
    </div>
  );
}
